import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';

export default function AssignmentCreate() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    maxScore: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '과제 제목을 입력해주세요.';
    }

    if (!formData.description.trim()) {
      newErrors.description = '과제 설명을 입력해주세요.';
    }

    if (formData.maxScore && (isNaN(formData.maxScore) || formData.maxScore < 0)) {
      newErrors.maxScore = '올바른 점수를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        ...formData,
        courseId: parseInt(courseId),
        maxScore: formData.maxScore ? parseInt(formData.maxScore) : null,
        dueDate: formData.dueDate || null,
      };

      const response = await api.post('/api/assignments', payload);

      if (response.data.success) {
        navigate(`/courses/${courseId}/assignments/${response.data.assignment.id}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || '과제 생성에 실패했습니다.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">새 과제 만들기</h1>
        <p className="text-neutral-600">학생들이 수행할 과제를 생성하세요.</p>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="과제 제목"
            name="title"
            value={formData.title}
            onChange={handleChange}
            error={errors.title}
            placeholder="예: 1단원 독후감 쓰기"
            required
          />

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              과제 설명
              <span className="text-danger-500 ml-1">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[150px]"
              placeholder="과제에 대한 상세한 설명을 입력하세요..."
            />
            {errors.description && (
              <p className="mt-1.5 text-sm text-danger-500">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="마감일 (선택사항)"
              name="dueDate"
              type="datetime-local"
              value={formData.dueDate}
              onChange={handleChange}
              error={errors.dueDate}
            />

            <Input
              label="배점 (선택사항)"
              name="maxScore"
              type="number"
              value={formData.maxScore}
              onChange={handleChange}
              error={errors.maxScore}
              placeholder="예: 100"
              min="0"
            />
          </div>

          {errors.submit && (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
              <p className="text-sm text-danger-600">{errors.submit}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? '생성 중...' : '과제 생성'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/courses/${courseId}`)}
              disabled={loading}
            >
              취소
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
